import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { SyncStatus } from "@/components/SyncStatus";
import "./analysis.css";

const TABS = [{ key: "ratings", label: "Team Ratings" }, { key: "leaders", label: "First Basket · Closers · Clutch" }, { key: "swings", label: "Ανατροπές · Διαφορές" }, { key: "backtest", label: "Backtest Ακρίβειας" }];

function Bar({ value, min, max, tone }) {
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return <div className="an-bar"><span className={`an-bar-fill an-${tone}`} style={{ width: `${pct}%` }} /></div>;
}

const edge = (b, m) => (b == null || m == null ? null : Math.round((b - m) * 100) / 100);
const EdgeTag = ({ v }) => v == null ? <span>—</span> :
  <span className={v > 0 ? "an-pos" : v < 0 ? "an-neg" : ""}>{v > 0 ? "+" : ""}{v} {v >= 0 ? "καλύτερο" : "χειρότερο"}</span>;

function PreTip({ data }) {
  if (!data) return <p className="an-empty">Φόρτωση…</p>;
  const s = data.summary;
  if (!s) return <p className="an-empty" data-testid="an-pretip-empty">Δεν υπάρχουν ακόμη βαθμολογημένα pre-tip snapshots (χρειάζονται αγώνες που έληξαν μετά την αποθήκευση της πρόβλεψης).</p>;
  const mc = data.market_compare || {};
  const CmpRow = ({ label, c }) => !c ? null : (
    <tr data-testid={`an-pretip-cmp-${label}`}>
      <td><b>{label === "total" ? "Σύνολο" : "Spread"}</b></td>
      <td>{c.model_mae}</td><td>{c.market_mae}</td>
      <td><EdgeTag v={edge(c.market_mae, c.model_mae)} /></td>
      <td>{c.model_better}/{c.n}</td>
    </tr>
  );
  return (
    <div className="an-pretip" data-testid="an-pretip">
      <p className="an-bt-note">
        <b>Πραγματική</b> pre-game πρόβλεψη του Rotation Model, παγωμένη πριν το tip-off και βαθμολογημένη vs το τελικό
        σκορ — <b>out-of-sample</b> (δεν «βλέπει» το αποτέλεσμα). {s.n} αγώνες. MAE = μέσο απόλυτο σφάλμα πόντων.
      </p>
      <div className="an-bt-total" data-testid="an-pretip-summary" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div><small>MAE Σύνολο</small><b>{s.mae_total}</b></div>
        <div><small>MAE Spread</small><b>{s.mae_spread}</b></div>
        <div><small>Σωστός νικητής</small><b>{s.winner_pct}%</b></div>
        <div><small>Bias Σύνολο</small><b className={s.bias_total > 0 ? "an-pos" : s.bias_total < 0 ? "an-neg" : ""}>{s.bias_total > 0 ? "+" : ""}{s.bias_total}</b></div>
      </div>
      {(mc.total || mc.spread) && (
        <table className="an-table" data-testid="an-pretip-market" style={{ minWidth: 540 }}>
          <thead><tr><th>Μέγεθος</th><th>MAE μοντέλου</th><th>MAE αγοράς</th><th>Edge</th><th>Μοντέλο καλύτερο</th></tr></thead>
          <tbody><CmpRow label="total" c={mc.total} /><CmpRow label="spread" c={mc.spread} /></tbody>
        </table>
      )}
      {data.by_band?.length > 0 && (
        <>
          <h3 className="an-section-h" data-testid="an-pretip-band-head" style={{ marginTop: 20 }}>Ακρίβεια ανά ζώνη δυσκολίας αντιπάλου</h3>
          <p className="an-bt-note">Σφάλμα πόντων της πρόβλεψης κάθε ομάδας, ομαδοποιημένο με βάση τη δυσκολία του αντιπάλου (A = δυσκολότερος → F = ευκολότερος). Δείχνει πού είναι πιο αξιόπιστη η πρόβλεψη.</p>
          <table className="an-table" data-testid="an-pretip-band" style={{ minWidth: 480 }}>
            <thead><tr><th>Ζώνη</th><th>Αντίπαλος</th><th>Team-games</th><th>MAE πόντων</th><th>Bias</th></tr></thead>
            <tbody>
              {data.by_band.map((b) => (
                <tr key={b.band} data-testid={`an-pretip-band-${b.band}`}>
                  <td><b>{b.band}</b></td><td>{b.label}</td><td>{b.n}</td><td>{b.mae_pts}</td>
                  <td className={b.bias_pts > 0 ? "an-pos" : b.bias_pts < 0 ? "an-neg" : ""}>{b.bias_pts > 0 ? "+" : ""}{b.bias_pts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Backtest({ data, k, onK, loading }) {
  const tEdge = edge(data.total.base_mae, data.total.mae);
  return (
    <div className="an-backtest" data-testid="an-backtest">
      <p className="an-bt-note">
        Σύγκριση πρόβλεψης vs πραγματικών σε {data.team_games} team-games (σεζόν {data.season}), με <b>leave-one-out</b>
        (κάθε αγώνας προβλέπεται από τους υπόλοιπους — χωρίς leakage). <b>MAE</b> = μέσο απόλυτο σφάλμα σε πόντους (μικρότερο = καλύτερο).
        Το «Baseline» είναι απλός ίσος διαμοιρασμός 25%/περίοδο· το «Raw» είναι χωρίς prior· το «Edge» δείχνει πόσο κερδίζει το μοντέλο.
      </p>

      <div className="an-slider" data-testid="an-bt-slider">
        <label>
          Ισχύς prior (βάρος εμπειρίας K) <b data-testid="an-bt-kval">{k}</b>
          {data.suggested_k != null && (
            <button type="button" className="an-k-suggest" data-testid="an-bt-k-suggest"
                    onClick={() => onK(data.suggested_k)} disabled={loading || k === data.suggested_k}>
              {k === data.suggested_k ? `✓ Βέλτιστο K=${data.suggested_k}` : `Αυτόματο → K=${data.suggested_k}`}
            </button>
          )}
          <span className="an-hint">0 = καθαρά εμπειρικό (θόρυβος νωρίς) · μεγαλύτερο = κλίση προς τον μ.ό. λίγκας (default {data.k_default}) · «Αυτόματο» = το K με το μικρότερο μέσο MAE/περίοδο{data.suggested_mae != null ? ` (${data.suggested_mae})` : ""}</span>
        </label>
        <input type="range" min="0" max="40" step="0.5" value={k} onChange={(e) => onK(Number(e.target.value))}
               disabled={loading} data-testid="an-bt-k-range" />
      </div>

      <div className="an-bt-total" data-testid="an-bt-total">
        <div><small>Σύνολο ομάδας · MAE μοντέλου</small><b>{data.total.mae}</b></div>
        <div><small>Baseline (μ.ό. λίγκας)</small><b>{data.total.base_mae}</b></div>
        <div><small>Edge</small><b><EdgeTag v={tEdge} /></b></div>
      </div>

      <table className="an-table" data-testid="an-bt-quarter">
        <thead><tr><th>Περίοδος</th><th>Μ.ό. πραγμ.</th><th>MAE μοντέλου</th><th>Raw (K=0)</th><th>Baseline 25%</th><th>Edge</th><th>Bias</th></tr></thead>
        <tbody>
          {data.quarter.map((q) => (
            <tr key={q.q} data-testid={`an-bt-q${q.q}`}>
              <td><b>Q{q.q}</b></td><td>{q.avg_actual}</td><td>{q.mae}</td><td>{q.raw_mae}</td><td>{q.base_mae}</td>
              <td><EdgeTag v={edge(q.base_mae, q.mae)} /></td>
              <td className={q.bias > 0 ? "an-pos" : q.bias < 0 ? "an-neg" : ""}>{q.bias > 0 ? "+" : ""}{q.bias}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table className="an-table" data-testid="an-bt-half" style={{ marginTop: 14 }}>
        <thead><tr><th>Ημίχρονο</th><th>MAE μοντέλου</th><th>Bias</th></tr></thead>
        <tbody>
          {data.half.map((h) => (
            <tr key={h.half} data-testid={`an-bt-h${h.half}`}>
              <td><b>{h.half === 1 ? "Α' ημίχρονο" : "Β' ημίχρονο"}</b></td><td>{h.mae}</td>
              <td className={h.bias > 0 ? "an-pos" : h.bias < 0 ? "an-neg" : ""}>{h.bias > 0 ? "+" : ""}{h.bias}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="an-section-h" data-testid="an-pretip-head">Backtest συνόλου · Pre-tip (out-of-sample)</h3>
      <PreTip data={data.pretip} />
    </div>
  );
}

function HalfTrend({ h1, h2 }) {
  if (h1 == null || h2 == null) return <span className="an-dim">—</span>;
  const d = Math.round((h2 - h1) * 10) / 10;
  const tone = d >= 1 ? "an-q-up" : d <= -1 ? "an-q-dn" : "an-q-eq";
  const arrow = d >= 1 ? "▲" : d <= -1 ? "▼" : "▬";
  const label = d >= 1 ? "Strong closer" : d <= -1 ? "Fast starter" : "Ισορροπημένη";
  return (
    <span className={`an-trend ${tone}`} data-testid="an-half-trend" title={label}>
      {arrow} {d > 0 ? "+" : ""}{d}
    </span>
  );
}

function QShare({ share, lg }) {
  if (!share || !share.length) return <span className="an-dim">—</span>;
  return (
    <div className="an-qshare" data-testid="an-qshare">
      {share.map((s, i) => {
        const pct = Math.round(s * 1000) / 10;
        const d = lg && lg[i] != null ? Math.round((s - lg[i]) * 1000) / 10 : null;
        const tone = d == null ? "" : d >= 0.3 ? "an-q-up" : d <= -0.3 ? "an-q-dn" : "an-q-eq";
        return (
          <span key={i} className={`an-q ${tone}`} title={d == null ? "" : `${d >= 0 ? "+" : ""}${d}% vs λίγκα`}>
            <small>Q{i + 1}</small>{pct}
          </span>
        );
      })}
    </div>
  );
}

function RatingsTable({ data }) {
  const lg = data.league || {};
  const lgQ = lg.q_share;
  const offs = data.teams.map((t) => t.ortg), defs = data.teams.map((t) => t.drtg), nets = data.teams.map((t) => t.net);
  const [oMin, oMax] = [Math.min(...offs), Math.max(...offs)];
  const [dMin, dMax] = [Math.min(...defs), Math.max(...defs)];
  const [nMin, nMax] = [Math.min(...nets), Math.max(...nets)];
  return (
    <div className="an-table-wrap">
      <div className="an-league" data-testid="an-league">
        Μέσος όρος λίγκας · ORtg <b>{lg.ortg}</b> · Pace <b>{lg.pace}</b>
        {lgQ && <span className="an-league-q" data-testid="an-league-qshare"> · Κατανομή περιόδων {lgQ.map((s, i) => <b key={i}>Q{i + 1} {Math.round(s * 1000) / 10}%</b>)}</span>}
        <span className="an-hint">ORtg/DRtg = πόντοι ανά 100 κατοχές (opponent-adjusted, καθαρισμένο garbage-time) · Κατανομή = shrunk ποσοστό πόντων ανά περίοδο (χρώμα = απόκλιση από λίγκα)</span>
      </div>
      <table className="an-table" data-testid="an-ratings-table">
        <thead>
          <tr><th>#</th><th>Ομάδα</th><th>GP</th><th>Net</th><th>ORtg</th><th>DRtg</th><th>Pace</th><th>Α' ημ.</th><th>Β' ημ.</th><th>Τάση ημιχ.</th><th>Κατανομή Q1–Q4 (%)</th></tr>
        </thead>
        <tbody>
          {data.teams.map((t) => (
            <tr key={t.team_id} data-testid={`an-team-${t.code}`}>
              <td className="an-rank">{t.net_rank}</td>
              <td className="an-team">{t.logo && <img src={t.logo} alt="" />}<b>{t.code}</b><span>{t.name}</span></td>
              <td>{t.gp}</td>
              <td className={`an-net ${t.net >= 0 ? "an-pos" : "an-neg"}`}>{t.net > 0 ? "+" : ""}{t.net}</td>
              <td><div className="an-cell"><span>{t.ortg}</span><Bar value={t.ortg} min={oMin} max={oMax} tone="off" /></div></td>
              <td><div className="an-cell"><span>{t.drtg}</span><Bar value={dMax - (t.drtg - dMin)} min={dMin} max={dMax} tone="def" /></div></td>
              <td>{t.pace}</td>
              <td>{t.h1_for}</td>
              <td>{t.h2_for}</td>
              <td><HalfTrend h1={t.h1_for} h2={t.h2_for} /></td>
              <td><QShare share={t.q_share} lg={lgQ} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeaderList({ title, subtitle, items, unit = "×", testid, invert = false, signed = false, onItem }) {
  const counts = items.map((p) => p.count);
  const top = Math.max(...counts, 1);
  const lo = Math.min(...counts, top);
  const maxAbs = Math.max(...counts.map((c) => Math.abs(c)), 1);
  return (
    <div className="an-leaders" data-testid={`an-leaders-${testid || title}`}>
      <h3>{title}</h3>
      <p className="an-sub">{subtitle}</p>
      {items.map((p, i) => (
        <div key={p.athlete_id} className={`an-leader-row${onItem ? " is-clickable" : ""}`}
          {...(onItem ? { role: "button", tabIndex: 0, onClick: () => onItem(p),
            onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && onItem(p),
            "data-testid": `an-row-${testid}-${i}` } : {})}>
          <span className="an-leader-rank">{i + 1}</span>
          {p.logo && <img src={p.logo} alt="" className="an-leader-logo" />}
          <span className="an-leader-name">{p.name || "—"}
            {p.avg != null && <em className="an-leader-avg"> {p.avg}/αγ.</em>}
            {signed && p.off != null && <em className="an-leader-avg"> {p.off}↑ / {p.def}↓</em>}
          </span>
          <span className="an-leader-code">{p.code}</span>
          <div className="an-leader-bar">
            <span className={signed ? (p.count >= 0 ? "an-bar-good" : "an-bar-bad") : invert ? "an-bar-good" : ""}
                  style={{ width: `${signed ? (Math.abs(p.count) / maxAbs) * 100 : invert ? (lo / p.count) * 100 : (p.count / top) * 100}%` }} />
          </div>
          <b className={`an-leader-count ${signed ? (p.count >= 0 ? "an-pos" : "an-neg") : ""}`}>{signed && p.count > 0 ? "+" : ""}{p.count}{unit}</b>
        </div>
      ))}
    </div>
  );
}

export default function Analysis() {
  const [tab, setTab] = useState("ratings");
  const [ratings, setRatings] = useState(null);
  const [leaders, setLeaders] = useState(null);
  const [swings, setSwings] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [pretip, setPretip] = useState(null);
  const [k, setK] = useState(8);
  const [btLoading, setBtLoading] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();
  const openGame = (p) => p.game_id && navigate(`/?day=${p.game_day || ""}&game=${p.game_id}`);

  useEffect(() => {
    api.get("/analysis/teams").then((r) => setRatings(r.data)).catch((e) => setErr(errorText(e)));
    api.get("/analysis/leaders").then((r) => setLeaders(r.data)).catch(() => {});
    api.get("/analysis/swings").then((r) => setSwings(r.data)).catch(() => {});
    api.get("/rotation/score-accuracy?limit=400").then((r) => setPretip(r.data)).catch(() => setPretip({ summary: null }));
  }, []);

  useEffect(() => {
    setBtLoading(true);
    api.get(`/analysis/backtest?k=${k}`).then((r) => setBacktest(r.data)).catch(() => {}).finally(() => setBtLoading(false));
  }, [k]);

  return (
    <div className="an-root" data-testid="analysis-page">
      <header className="an-head">
        <div>
          <p className="an-eyebrow">ADVANCED ANALYTICS</p>
          <h1>Team Analysis</h1>
          <p className="an-desc">Από play-by-play: ρυθμός, αποδοτικότητα, κατανομή ημιχρόνου και ποιος σκοράρει πρώτος/τελευταίος.</p>
        </div>
        {ratings?.updated_at && <SyncStatus lastSync={ratings.updated_at} canRefresh={false} testid="analysis-sync" />}
      </header>

      <nav className="an-tabs" data-testid="analysis-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? "is-active" : ""} onClick={() => setTab(t.key)} data-testid={`an-tab-${t.key}`}>{t.label}</button>
        ))}
      </nav>

      {err && <p className="an-empty" data-testid="analysis-error">{err}</p>}

      {tab === "ratings" && (ratings ? (ratings.teams.length ? <RatingsTable data={ratings} /> : <p className="an-empty">Δεν υπάρχουν ακόμη δεδομένα play-by-play.</p>) : <p className="an-empty">Φόρτωση…</p>)}

      {tab === "leaders" && (leaders ? (
        <div className="an-leaders-grid">
          <LeaderList title="First Basket" subtitle={`Ποιος σκοράρει πρώτος · ${leaders.games} αγώνες`} items={leaders.first_basket} />
          <LeaderList title="Closers" subtitle="Ποιος σκοράρει το τελευταίο καλάθι" items={leaders.closers} />
          <LeaderList title="Clutch" subtitle="Πόντοι στο 4ο δεκάλεπτο · σύνολο σεζόν" items={leaders.clutch || []} unit=" π." testid="clutch" />
          <LeaderList title="Clutch Ομάδες" subtitle="Ομάδες με τους περισσότερους πόντους στο 4ο δεκάλεπτο · μ.ό./αγώνα" items={leaders.clutch_teams || []} unit=" π." testid="clutch-teams" />
          <LeaderList title="Clutch Άμυνα" subtitle="Ομάδες που δέχονται τους λιγότερους πόντους στο 4ο δεκάλεπτο · μ.ό./αγώνα" items={leaders.clutch_def_teams || []} unit=" π." testid="clutch-def-teams" invert />
          <LeaderList title="Clutch Net" subtitle="Καθαρό πλεόνασμα 4ου δεκαλέπτου (πόντοι − δέχεται) · μ.ό./αγώνα" items={leaders.clutch_net_teams || []} unit=" π." testid="clutch-net-teams" signed />
          <LeaderList title="2ο Ημίχρονο · Επίθεση" subtitle="Ομάδες με τους περισσότερους πόντους στο 2ο ημίχρονο · μ.ό./αγώνα" items={leaders.h2_off_teams || []} unit=" π." testid="h2-off-teams" />
          <LeaderList title="2ο Ημίχρονο · Άμυνα" subtitle="Ομάδες που δέχονται τους λιγότερους πόντους στο 2ο ημίχρονο · μ.ό./αγώνα" items={leaders.h2_def_teams || []} unit=" π." testid="h2-def-teams" invert />
        </div>
      ) : <p className="an-empty">Φόρτωση…</p>)}

      {tab === "backtest" && (backtest ? (backtest.team_games ? <Backtest data={{ ...backtest, pretip }} k={k} onK={setK} loading={btLoading} /> : <p className="an-empty">Δεν υπάρχουν αρκετά δεδομένα για backtest ακόμη.</p>) : <p className="an-empty">Φόρτωση…</p>)}

      {tab === "swings" && (swings ? (swings.games ? (
        <div className="an-leaders-grid">
          <LeaderList title="Μεγαλύτερες Ανατροπές" subtitle="Ομάδες που γύρισαν τη μεγαλύτερη διαφορά και νίκησαν · στα όρια περιόδων" items={swings.comebacks} unit=" π." testid="comebacks" onItem={openGame} />
          <LeaderList title="Δέχτηκαν Ανατροπή" subtitle="Ομάδες που έχασαν ενώ προηγούνταν με τη μεγαλύτερη διαφορά" items={swings.blown} unit=" π." testid="blown" onItem={openGame} />
          <LeaderList title="Μεγαλύτερες Νίκες" subtitle="Νίκες με τη μεγαλύτερη τελική διαφορά πόντων" items={swings.wins} unit=" π." testid="wins" onItem={openGame} />
          <LeaderList title="Μεγαλύτερες Ήττες" subtitle="Ήττες με τη μεγαλύτερη τελική διαφορά πόντων" items={swings.losses} unit=" π." testid="losses" onItem={openGame} />
          <LeaderList title="Μεγαλύτερα Θρίλερ" subtitle="Πιο αμφίρροπα παιχνίδια · νίκη με 1-2 πόντους ή στην παράταση" items={swings.thrillers} unit=" π." testid="thrillers" invert onItem={openGame} />
          <LeaderList title="Μ.Ο. Διαφοράς στις Νίκες" subtitle="Με πόση διαφορά κερδίζει κατά μ.ό. κάθε ομάδα (≥2 νίκες)" items={swings.avg_win} unit=" π." testid="avg-win" />
          <LeaderList title="Μ.Ο. Διαφοράς στις Ήττες" subtitle="Με πόση διαφορά χάνει κατά μ.ό. κάθε ομάδα (≥2 ήττες)" items={swings.avg_loss} unit=" π." testid="avg-loss" />
          <LeaderList title="Σερί Νικών" subtitle="Μεγαλύτερα συνεχόμενα σερί νικών της σεζόν" items={swings.win_streaks} unit=" στη σειρά" testid="win-streaks" />
          <LeaderList title="Σερί Ηττών" subtitle="Μεγαλύτερα συνεχόμενα σερί ηττών της σεζόν" items={swings.loss_streaks} unit=" στη σειρά" testid="loss-streaks" />
        </div>
      ) : <p className="an-empty">Δεν υπάρχουν ακόμη δεδομένα play-by-play.</p>) : <p className="an-empty">Φόρτωση…</p>)}
    </div>
  );
}
